import type { BodyMetrics, Point } from '../HumanoidGeometry';
import { createCircle, darken } from './svgPrims';
import type { Painter } from './NpcFinish';
import type { IdlePhase, NpcProp } from './HumanoidTypes';
import { isRoleProp, renderRoleProp } from './NpcRoleProps';

/**
 * Held and worn NPC props, in the canonical 140×120 humanoid space (the figure
 * faces right; the back is the left side). Props that move with the wind read
 * `phase` (0–3) so a NPC can cycle a few secondary-motion frames.
 */
export interface PropContext {
  m: BodyMetrics;
  paint: Painter;
  phase: IdlePhase;
  frontHand: Point;
  backHand?: Point;
  /** Plan-12 fit: straps wrap the whole body, held props sit behind the gripping hand. */
  fit?: boolean;
}

export interface PropLayers {
  /** Behind the body (slung bows, planted spears/staffs, scarf tails). */
  back: string;
  /** Over the garment, under the face (straps, scarf wraps). Clipped to the body when fitted. */
  clothing: string;
  /** Over the clothing, under the face, never clipped (a net slung over the shoulder). */
  worn: string;
  /** Topmost (canes, satchels, lanterns, crystal glow). */
  front: string;
}

/** Sideways sway per phase: a slow back-and-forth. */
const SWAY: Record<IdlePhase, number> = { 0: 0, 1: 1, 2: 0, 3: -1 };

function bow(color: string): string {
  return `<g transform="rotate(-14 24 60)">
        <path d="M24,20 Q0,60 24,100" fill="none" stroke="${color}" stroke-width="4.5" stroke-linecap="round" />
        <path d="M24,22 L24,98" stroke="#d8d8c8" stroke-width="1.2" />
      </g><g transform="rotate(24 96 30)">
        <path d="M92,16 L92,5 M97,15 L97,3 M102,16 L102,6" stroke="#8a7a5a" stroke-width="1.6" />
        <circle cx="92" cy="5" r="2" fill="#aab3a6" /><circle cx="97" cy="3" r="2" fill="#aab3a6" /><circle cx="102" cy="6" r="2" fill="#aab3a6" />
        <rect x="88" y="14" width="15" height="32" rx="4" fill="${darken(color, 0.8)}" />
        <rect x="88" y="18" width="15" height="4" fill="${darken(color, 0.6)}" />
      </g>`;
}

/** A survey spear planted at the front hand: tall shaft, iron head, swaying pennant. */
function spear(prop: Extract<NpcProp, { kind: 'spear' }>, ctx: PropContext): string {
  const x = ctx.frontHand.x + 3;
  const sway = SWAY[ctx.phase];
  const flag = ctx.paint.fill(prop.pennantColor);
  // Fitted spears stand a little shorter so large bodies keep the tip inside the frame.
  const top = ctx.fit ? 10 : 0;
  const head = `<path d="M${x},-6 L${x + 4},4 L${x},8 L${x - 4},4 Z" fill="#c9d2da" />
      <path d="M${x},-6 L${x + 4},4 L${x},4 Z" fill="#eef4f8" opacity="0.8" />
      <path data-npc-sway="pennant" d="M${x + 1},10 Q${x + 12},${11 + sway} ${x + 22},${14 + sway * 2} Q${x + 13},${17 + sway} ${x + 21},${22 + sway * 2} Q${x + 11},${22 + sway} ${x + 1},${26} Z" fill="${flag}" />
      <path d="M${x + 1},12 L${x + 1},25" stroke="${darken(prop.pennantColor, 0.6)}" stroke-width="1.4" />`;
  return `<g data-npc-prop="spear">
      <path d="M${x},114 L${x},${6 + top}" stroke="${darken(prop.color, 0.7)}" stroke-width="4.2" stroke-linecap="round" />
      <path d="M${x - 0.8},112 L${x - 0.8},${8 + top}" stroke="${prop.color}" stroke-width="2" stroke-linecap="round" />
      ${top ? `<g transform="translate(0 ${top})">${head}</g>` : head}
    </g>`;
}

/** A walking staff crowned with a glowing crystal (glow drawn in front). */
function crystalStaff(prop: Extract<NpcProp, { kind: 'crystal_staff' }>, ctx: PropContext): { back: string; front: string } {
  const x = ctx.frontHand.x + 3;
  const c = prop.crystalColor;
  return {
    back: `<g data-npc-prop="crystal_staff">
      <path d="M${x},114 Q${x + 2},70 ${x - 1},24" fill="none" stroke="${darken(prop.color, 0.7)}" stroke-width="4" stroke-linecap="round" />
      <path d="M${x - 1},24 Q${x - 7},18 ${x - 3},10 M${x - 1},24 Q${x + 6},17 ${x + 3},9" fill="none" stroke="${prop.color}" stroke-width="2.4" stroke-linecap="round" />
    </g>`,
    front: `<g data-npc-prop="crystal">
      <circle cx="${x}" cy="13" r="11" fill="${c}" opacity="0.18" />
      <path d="M${x},2 L${x + 5},11 L${x},22 L${x - 5},11 Z" fill="${c}" />
      <path d="M${x},2 L${x + 5},11 L${x},11 Z" fill="#ffffff" opacity="0.65" />
    </g>`,
  };
}

/** A scarf: wrap at the collar plus a two-strand tail streaming behind. */
function scarf(prop: Extract<NpcProp, { kind: 'scarf' }>, ctx: PropContext): { back: string; clothing: string } {
  const { m } = ctx;
  const s = SWAY[ctx.phase];
  const fill = ctx.paint.fill(prop.color);
  const stripe = prop.stripeColor ?? darken(prop.color, 0.75);
  const x0 = m.left + 8;
  return {
    back: `<g data-npc-prop="scarf-tail" data-npc-sway="scarf">
      <path d="M${x0 + 10},58 Q${x0 - 8},${58 + s * 3} ${x0 - 30},${50 + s * 5} L${x0 - 27},${62 + s * 5} Q${x0 - 8},${68 + s * 3} ${x0 + 10},68 Z" fill="${fill}" />
      <path d="M${x0 + 8},66 Q${x0 - 6},${74 + s * 2} ${x0 - 20},${76 + s * 4} L${x0 - 18},${84 + s * 4} Q${x0 - 4},${81 + s * 2} ${x0 + 8},74 Z" fill="${darken(prop.color, 0.82)}" />
      <path d="M${x0 - 24},${53 + s * 5} L${x0 - 22},${61 + s * 5} M${x0 - 15},${78 + s * 4} L${x0 - 14},${83 + s * 4}" stroke="${stripe}" stroke-width="2.2" />
    </g>`,
    clothing: `<g data-npc-prop="scarf-wrap">
      <path d="M${m.left - (ctx.fit ? 16 : -10)},58 Q${m.centerX},68 ${m.right + (ctx.fit ? 16 : -10)},58 L${m.right + (ctx.fit ? 16 : -11)},66 Q${m.centerX},76 ${m.left - (ctx.fit ? 16 : -11)},66 Z" fill="${fill}" />
      <path d="M${m.left + 12},62 Q${m.centerX},72 ${m.right - 12},62" fill="none" stroke="${stripe}" stroke-width="1.6" />
    </g>`,
  };
}

/** A fishing net slung over the back shoulder. */
function net(prop: Extract<NpcProp, { kind: 'net' }>, ctx: PropContext): string {
  const { m } = ctx;
  const s = SWAY[ctx.phase] * 0.6;
  const x = m.left + 12;
  const mesh = darken(prop.color, 0.7);
  return `<g data-npc-prop="net">
      <path d="M${x + 14},50 Q${x - 6},56 ${x - 8 + s},84 Q${x + 2},${96 + s} ${x + 12},88 Q${x + 18},70 ${x + 22},54 Z" fill="${prop.color}" opacity="0.92" />
      <path d="M${x + 10},55 L${x - 4 + s},86 M${x + 16},57 L${x + 4},92 M${x + 20},62 L${x + 12},88 M${x - 2},64 L${x + 20},60 M${x - 5},74 L${x + 18},70 M${x - 6},84 L${x + 14},80" stroke="${mesh}" stroke-width="1.1" fill="none" />
      <circle cx="${x - 6 + s}" cy="${88 + s}" r="2.2" fill="#c8b878" />
    </g>`;
}

/** A gnarled walking stick with a crook under the front hand. */
function cane(color: string, ctx: PropContext): string {
  const x = ctx.frontHand.x + 2;
  const knot = darken(color, 0.7);
  return `<g data-npc-prop="cane">
      <path d="M${x + 4},113 Q${x + 1},90 ${x + 2},70 Q${x + 3},62 ${x - 1},56" fill="none" stroke="${knot}" stroke-width="5" stroke-linecap="round" />
      <path d="M${x + 3.4},112 Q${x + 0.6},90 ${x + 1.4},70" fill="none" stroke="${color}" stroke-width="2.4" stroke-linecap="round" />
      <path d="M${x - 1},56 Q${x - 5},50 ${x - 10},53 Q${x - 13},56 ${x - 10},59" fill="none" stroke="${knot}" stroke-width="4.4" stroke-linecap="round" />
      <circle cx="${x + 2.6}" cy="86" r="1.6" fill="${knot}" /><circle cx="${x + 1.8}" cy="100" r="1.3" fill="${knot}" />
    </g>`;
}

export function renderNpcProps(props: NpcProp[] | undefined, ctx: PropContext): PropLayers {
  const out: PropLayers = { back: '', clothing: '', worn: '', front: '' };
  const { m } = ctx;
  for (const prop of props ?? []) {
    if (isRoleProp(prop)) {
      renderRoleProp(prop, ctx, out);
      continue;
    }
    switch (prop.kind) {
    case 'bow':
      out.back += bow(prop.color);
      break;
    case 'spear':
      out.back += spear(prop, ctx);
      break;
    case 'crystal_staff': {
      const staff = crystalStaff(prop, ctx);
      out.back += staff.back;
      out.front += staff.front;
      break;
    }
    case 'scarf': {
      const s = scarf(prop, ctx);
      out.back += s.back;
      out.clothing += s.clothing;
      break;
    }
    case 'net':
      // Over the garment on the back shoulder; the face still draws on top.
      out[ctx.fit ? 'worn' : 'clothing'] += net(prop, ctx);
      break;
    case 'cane':
      out[ctx.fit ? 'back' : 'front'] += cane(prop.color, ctx);
      break;
    case 'satchel': {
      // Strap rides in the clothing layer (under the face) so it never
      // crosses the mouth; the bag itself stays on top.
      out.clothing += ctx.fit
        ? `<path data-npc-strap="satchel" d="M${m.left - 4},44 Q${m.centerX},${66} ${m.right + 6},98" stroke="${prop.color}" stroke-width="4.5" fill="none" />`
        : `<path d="M${m.left + 18},50 L${m.right - 20},88" stroke="${prop.color}" stroke-width="4.5" stroke-linecap="round" />`;
      const bagX = m.right - 32;
      if (prop.mapPeek) {
        out.front += `<g transform="rotate(12 ${bagX + 18} 80)">
            <rect x="${bagX + 12}" y="75" width="12" height="10" fill="#e8d8a0" />
            <path d="M${bagX + 13.5},78 L${bagX + 22},78 M${bagX + 13.5},80.5 L${bagX + 20},80.5" stroke="#8a7050" stroke-width="0.9" />
          </g>`;
      }
      out.front += `<rect x="${bagX}" y="84" width="24" height="17" rx="4" fill="${prop.color}" />`;
      out.front += `<rect x="${bagX}" y="84" width="24" height="7" rx="3" fill="${darken(prop.color, 0.78)}" />`;
      out.front += createCircle(bagX + 12, 93, 1.8, '#c8b878');
      break;
    }
    case 'lantern': {
      const glow = prop.glowColor ?? '#ffcc00';
      out.front += createCircle(m.right - 8, 86, 9, glow, 'none', 0, 0.22);
      out.front += `<rect x="${m.right - 13.5}" y="74.5" width="4" height="4" fill="#2a2a2a" />`;
      out.front += `<rect x="${m.right - 17}" y="78" width="11" height="15" rx="2" fill="#2a2a2a" />`;
      out.front += `<rect x="${m.right - 15}" y="81" width="7" height="9.5" rx="1" fill="${glow}" />`;
      break;
    }
    }
  }
  return out;
}
